import { useState, useEffect, lazy, Suspense } from "react";
import { useLowestDocPrice } from "@/utils/docPrices";
import AppLayout from "@/components/AppLayout";
import AppSeo from "@/components/AppSeo";
import { IonSkeletonText } from "@ionic/react";
import { generateCeaseAndDesistPreview } from "@/utils/ceaseAndDesistPreviewGenerator";
import { generatePowerOfAttorneyPreview } from "@/utils/powerOfAttorneyPreviewGenerator";
import { generateVehicleBillOfSalePreview } from "@/utils/vehicleBillOfSalePreviewGenerator";
import AppTaxFormModal from "./AppTaxFormModal";
import { LEGAL_FORM_CONFIGS } from "./appLegalFormConfigs";
import { useDisabledGenerators, useGeneratorGate, LEGAL_GENERATOR_IDS } from "@/utils/generatorAvailability";
import { loadJsPDF } from "@/utils/pdfEngines";
import { fetchPublishedLayout, renderLayout, getSampleVariants } from "@/utils/layoutEngine";
import { pdfToPageImages } from "@/utils/pdfPageImages";
import { userHeaders, isUserLoggedIn } from "@/utils/userAuth";
// A business's own legal design opens its fill-in modal in place (the same
// form-then-preview modal as the forms above).
const CustomDocEmbed = lazy(() => import("./AppCustomDocs"));
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Sample data for the card previews — the real generators render these.
const CEASE_SAMPLE = {
  template: "professional", primaryColor: "#1a1a1a", accentColor: "#b71c1c",
  senderName: "John Smith", senderAddress: "123 Main Street",
  senderCity: "New York", senderState: "NY", senderZip: "10001",
  senderPhone: "(555) 123-4567", senderEmail: "john@example.com",
  recipientName: "Jane Doe", recipientCompany: "Acme Collections LLC",
  recipientAddress: "456 Business Ave", recipientCity: "New York", recipientState: "NY", recipientZip: "10002",
  letterDate: "2026-09-01", violationType: "harassment",
  description: "Repeated unwanted phone calls and messages at all hours despite multiple requests to stop contacting me.",
  complianceDays: "10", deliveryMethod: "certified-mail", signatureName: "John Smith",
};
const POA_SAMPLE = {
  governingState: "NY",
  principalName: "John Smith", principalAddress: "123 Main Street",
  principalCity: "New York", principalState: "NY", principalZip: "10001",
  agentName: "Jane Smith", agentRelationship: "Spouse",
  agentAddress: "123 Main Street", agentCity: "New York", agentState: "NY", agentZip: "10001",
  grantAllPowers: true, powers: {}, effectiveType: "immediate",
  effectiveDate: "2026-09-01", executionDate: "2026-09-01",
  agentCompensation: "uncompensated", revokePrior: true,
  includeWitnesses: true, includeNotary: true, includeAgentAcceptance: true,
};
const VEHICLE_SAMPLE = {
  template: "classic", primaryColor: "#1a1a4d", accentColor: "#3333aa",
  state: "NY", county: "New York", saleDate: "2026-09-01",
  sellerName: "John Smith", sellerAddress: "123 Main Street",
  sellerCity: "New York", sellerState: "NY", sellerZip: "10001",
  buyerName: "Jane Doe", buyerAddress: "456 Oak Avenue",
  buyerCity: "Brooklyn", buyerState: "NY", buyerZip: "11201",
  vehicleYear: "2019", vehicleMake: "Toyota", vehicleModel: "Camry SE",
  vehicleVin: "4T1B11HK5KU211234", vehicleColor: "Silver", vehicleBodyType: "Sedan",
  odometerReading: "48250", salePrice: "14500", paymentMethod: "Certified Check",
  odometerDisclosure: "actual", conditionType: "as-is",
};

const LEGAL_FORMS = [
  { key: "cease-and-desist", name: "Cease & Desist", title: "Cease and Desist Letter", color: "#b91c1c",
    preview: () => generateCeaseAndDesistPreview(CEASE_SAMPLE) },
  { key: "power-of-attorney", name: "Power of Attorney", title: "Durable Power of Attorney", color: "#7c3aed",
    preview: () => generatePowerOfAttorneyPreview({ ...POA_SAMPLE }) },
  { key: "vehicle-bill-of-sale", name: "Bill of Sale", title: "Vehicle Bill of Sale", color: "#dc2626",
    preview: () => generateVehicleBillOfSalePreview(VEHICLE_SAMPLE) },
];

export default function AppLegalForms() {
  const seoPrice = useLowestDocPrice(["cease-and-desist", "power-of-attorney", "vehicle-bill-of-sale"]); // the cheapest, as the admin prices them
  const [previews, setPreviews] = useState({});
  const [loadingPreviews, setLoadingPreviews] = useState(true);
  const [activeForm, setActiveForm] = useState(null);
  const disabledGenerators = useDisabledGenerators();
  useGeneratorGate(LEGAL_GENERATOR_IDS); // every legal form hidden from this visitor: back to /app
  const visibleForms = LEGAL_FORMS.filter(form => !disabledGenerators.has(form.key));

  // The business's own published legal designs (Templates, filed under Legal
  // Documents) show here as cards of their own.
  const [ownTemplates, setOwnTemplates] = useState([]);
  const [ownPreviews, setOwnPreviews] = useState({});
  const [embed, setEmbed] = useState(null); // template id while its form is open
  useEffect(() => {
    if (!isUserLoggedIn()) return;
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/user/doc-templates?documentType=legal-document&published=1`, { headers: userHeaders() })
      .then((r) => r.json())
      .then((d) => { if (!cancelled && d && d.success && d.hasAccess) setOwnTemplates(d.templates || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const t of ownTemplates) {
        if (ownPreviews[t.id]) continue;
        try {
          const layout = await fetchPublishedLayout(t.id, undefined, { user: true });
          if (cancelled || !layout) continue;
          const doc = new (await loadJsPDF())({ unit: "pt", format: "letter" });
          const base = t.baseDocType || "legal-document";
          renderLayout(doc, layout, getSampleVariants(base)[0].data, base);
          const imgs = await pdfToPageImages(doc.output("arraybuffer"));
          if (cancelled) return;
          if (imgs && imgs[0]) setOwnPreviews((p) => ({ ...p, [t.id]: imgs[0] }));
        } catch (e) { console.error("Legal template preview failed:", e); }
      }
    })();
    return () => { cancelled = true; };
  }, [ownTemplates]); // eslint-disable-line

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const form of LEGAL_FORMS) {
        try {
          const img = await form.preview();
          if (cancelled) return;
          if (img) setPreviews(prev => ({ ...prev, [form.key]: img }));
        } catch (err) {
          console.error(`Legal form preview failed for ${form.key}:`, err);
        }
      }
      if (!cancelled) setLoadingPreviews(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <AppLayout fillHeight>
      <AppSeo title="Legal Document Generator" description="Create cease and desist letters, power of attorney forms, vehicle bills of sale, and more in minutes with instant PDF download." path="/app/legal-forms" price={seoPrice} appName="MintSlip Legal Document Generator" />
      <div style={{ padding: 10, height: "100%", boxSizing: "border-box" }}>
        <div style={{ background: "var(--ion-card-background)", borderRadius: 6, padding: "20px 20px 24px", height: "100%", overflowY: "auto", boxShadow: "0 2px 12px rgba(0,0,0,0.10)", boxSizing: "border-box" }}>
          {visibleForms.length === 0 && ownTemplates.length === 0 && !loadingPreviews && (
            <p style={{ color: "var(--ion-color-medium)", fontSize: "0.9rem", textAlign: "center", padding: "32px 0" }}>
              No forms are currently available.
            </p>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
            {visibleForms.map((form, cardIdx) => (
              <div key={form.key} className="app-sample-card ion-activatable"
                onClick={() => setActiveForm(form.key)}
                style={{ animationDelay: `${cardIdx * 60}ms`, cursor: "pointer", borderRadius: 10, border: "1.5px solid var(--app-divider, rgba(0,0,0,0.12))", background: "var(--ion-card-background)", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", transition: "box-shadow 0.2s, transform 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.18)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "none"; }}
              >
                <ion-ripple-effect />
                <div style={{ position: "relative", background: "#fff", overflow: "hidden", minHeight: 160 }}>
                  <div style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: form.color, color: "#fff", padding: "4px 12px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, boxShadow: "0 2px 8px rgba(0,0,0,0.28)" }}>
                    {form.name}
                  </div>
                  {previews[form.key] ? (
                    <div style={{ position: "relative", paddingTop: "141.4%", overflow: "hidden", pointerEvents: "none" }}>
                      <img src={previews[form.key]} alt={`${form.name} sample`} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block" }} />
                      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 56, background: "linear-gradient(to top, rgba(0,0,0,0.28), transparent)" }} />
                    </div>
                  ) : loadingPreviews ? (
                    <div style={{ position: "relative", paddingTop: "141.4%", overflow: "hidden" }}>
                      <IonSkeletonText animated={true} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", margin: 0, borderRadius: 0, "--background": "rgba(0, 0, 0, 0.085)", "--background-rgb": "18, 18, 18" }} />
                    </div>
                  ) : (
                    <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb" }}>
                      <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}>Preview unavailable</span>
                    </div>
                  )}
                </div>
                <div style={{ padding: "8px 16px 12px", textAlign: "center", borderTop: "1px solid var(--app-divider, rgba(0,0,0,0.06))" }}>
                  <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--ion-color-primary)" }}>Create {form.name} →</span>
                </div>
              </div>
            ))}
            {ownTemplates.map((t, i) => (
              <div key={t.id} className="app-sample-card ion-activatable"
                onClick={() => setEmbed(t.id)}
                style={{ animationDelay: `${(visibleForms.length + i) * 60}ms`, cursor: "pointer", borderRadius: 10, border: "1.5px solid var(--app-divider, rgba(0,0,0,0.12))", background: "var(--ion-card-background)", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", transition: "box-shadow 0.2s, transform 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.18)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "none"; }}
              >
                <ion-ripple-effect />
                <div style={{ position: "relative", background: "#fff", overflow: "hidden", minHeight: 160 }}>
                  <div style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: t.badgeColor || "#34d399", color: "#fff", padding: "4px 12px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, boxShadow: "0 2px 8px rgba(0,0,0,0.28)" }}>
                    {t.name}
                  </div>
                  {ownPreviews[t.id] ? (
                    <div style={{ position: "relative", paddingTop: "141.4%", overflow: "hidden", pointerEvents: "none" }}>
                      <img src={ownPreviews[t.id]} alt={`${t.name} sample`} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block" }} />
                      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 56, background: "linear-gradient(to top, rgba(0,0,0,0.28), transparent)" }} />
                    </div>
                  ) : (
                    <div style={{ position: "relative", paddingTop: "141.4%", overflow: "hidden" }}>
                      <IonSkeletonText animated={true} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", margin: 0, borderRadius: 0, "--background": "rgba(0, 0, 0, 0.085)", "--background-rgb": "18, 18, 18" }} />
                    </div>
                  )}
                </div>
                <div style={{ padding: "8px 16px 12px", textAlign: "center", borderTop: "1px solid var(--app-divider, rgba(0,0,0,0.06))" }}>
                  <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--ion-color-primary)" }}>Create {t.name} →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {activeForm && <AppTaxFormModal config={LEGAL_FORM_CONFIGS[activeForm]} onClose={() => setActiveForm(null)} />}
      {embed && (
        <Suspense fallback={null}>
          <CustomDocEmbed embedDocumentType="legal-document" embedTemplateId={embed} onEmbedClose={() => setEmbed(null)} />
        </Suspense>
      )}
    </AppLayout>
  );
}
